import type { AgentMessage, AgentModelResponse } from "@calcplan/ai-capabilities";

const ACTION_RESPONSE_MAX_CHARS = 360;
const ACTION_RESPONSE_MAX_SENTENCES = 2;
const STREAM_PROGRESS_MAX_CHARS = 96;
const TECHNICAL_ACTION_LANGUAGE = /\b(?:implantar elemento por rela[cç][aã]o|instalar m[oó]dulo por rela[cç][aã]o|definir estado de partes m[oó]veis|confer[eê]ncia geom[eé]trica|visual independente|chamadas? de ferramenta|capabilit(?:y|ies))\b/iu;

const ACTION_RESULT_RULES: Array<{ pattern: RegExp; result: string }> = [
  {
    pattern: /\b(?:implantar elemento por rela[cç][aã]o|mover elemento|ajustar posi[cç][aã]o por deslocamento|fixar elemento na parede|girar elemento)\b/iu,
    result: "posicionei os elementos solicitados",
  },
  {
    pattern: /\b(?:instalar m[oó]dulo por rela[cç][aã]o|inserir m[oó]dulo)\b/iu,
    result: "instalei os módulos no ambiente",
  },
  {
    pattern: /\b(?:definir estado de partes m[oó]veis|interagir com pe[cç]a din[aâ]mica)\b/iu,
    result: "ajustei portas, gavetas e outras partes móveis",
  },
  {
    pattern: /\b(?:redimensionar elemento|ajustar dimens(?:[aã]o|ões)|alterar medida)\b/iu,
    result: "ajustei as medidas",
  },
  {
    pattern: /\b(?:aplicar material|igualar acabamento|alterar acabamento)\b/iu,
    result: "ajustei os acabamentos",
  },
  {
    pattern: /\b(?:desenhar parede|ajustar parede|mover encontro de paredes)\b/iu,
    result: "ajustei as paredes",
  },
  {
    pattern: /\b(?:inserir|ajustar|mover) (?:porta|janela|abertura)\b/iu,
    result: "ajustei as aberturas",
  },
  {
    pattern: /\b(?:adicionar|ajustar|atualizar) cota\b/iu,
    result: "atualizei as medidas exibidas",
  },
  {
    pattern: /\b(?:remover|excluir|apagar) (?:elemento|item|m[oó]dulo)\b/iu,
    result: "removi os itens solicitados",
  },
  {
    pattern: /\b(?:duplicar|copiar) (?:elemento|item|m[oó]dulo)\b/iu,
    result: "dupliquei os itens solicitados",
  },
];

/**
 * Tool-bound text is working narration, not the answer. Keeping it out of the
 * next request avoids a growing transcript of the model's internal process.
 */
export function withoutToolNarration(messages: AgentMessage[]): AgentMessage[] {
  return messages.map((message) => {
    if (message.role !== "assistant" || !message.content.some((block) => block.type === "tool_use")) {
      return message;
    }
    return {
      ...message,
      content: message.content.filter((block) => block.type !== "text"),
    };
  });
}

export function assistantText(message: AgentModelResponse): string {
  return message.content
    .filter((block) => block.type === "text")
    .map((block) => block.text)
    .join("\n")
    .trim();
}

/**
 * Turns the model's streamed, user-facing narration into one transient status.
 * The complete text is still kept only in the provider response; the panel
 * replaces this phrase on every update instead of building a visible transcript.
 */
export function streamProgressText(text: string): string | null {
  const normalized = text
    .replace(/```[\s\S]*?```/g, " ")
    .replace(/[`*_#>]/g, " ")
    .replace(/^\s*(?:atualiza[cç][aã]o|progresso|status)\s*:\s*/iu, "")
    .replace(/\s+/g, " ")
    .trim();
  if (!normalized) return null;
  if (/[{}\[\]]/.test(normalized) || /\b(?:scene|catalog|capability|ui)\.[a-z0-9_.-]+\b/i.test(normalized)) return null;
  if (/\b(?:racioc[ií]nio interno|cadeia de pensamento|chain of thought|passo a passo interno)\b/i.test(normalized)) return null;

  const phrases = normalized.split(/(?<=[.!?])\s+/u).filter(Boolean);
  const current = phrases.at(-1) ?? normalized;
  if (current.length <= STREAM_PROGRESS_MAX_CHARS) return current;

  const clipped = current.slice(0, STREAM_PROGRESS_MAX_CHARS - 1);
  const lastSpace = clipped.lastIndexOf(" ");
  return `${clipped.slice(0, Math.max(lastSpace, 1)).trimEnd()}…`;
}

export function compactActionResponse(text: string): string {
  const normalized = normalizeActionResponse(text);
  if (!normalized) return "";

  const sentences = actionSentences(normalized);
  const concise = sentences.slice(0, ACTION_RESPONSE_MAX_SENTENCES).join(" ").trim();
  if (concise.length <= ACTION_RESPONSE_MAX_CHARS) return concise;

  // The normal path asks the model to rewrite before reaching this fallback.
  // If a provider still returns one oversized sentence, keep whole clauses and
  // close the phrase explicitly; never expose a mechanically cut word or “…”.
  const clauses = concise.split(/;\s+|,\s+(?=(?:mas|porém|contudo|além disso|e também|com isso)\b)/iu);
  let complete = "";
  for (const clause of clauses) {
    const candidate = complete ? `${complete}; ${clause}` : clause;
    if (candidate.length > ACTION_RESPONSE_MAX_CHARS) break;
    complete = candidate;
  }
  if (!complete) return concise;
  return /[.!?]$/u.test(complete) ? complete : `${complete}.`;
}

export function actionResponseNeedsRewrite(text: string): boolean {
  const normalized = normalizeActionResponse(text);
  return !normalized
    || normalized.length > ACTION_RESPONSE_MAX_CHARS
    || actionSentences(normalized).length > ACTION_RESPONSE_MAX_SENTENCES
    || TECHNICAL_ACTION_LANGUAGE.test(normalized);
}

export function actionResponseRewriteInstruction(): string {
  return `Reescreva somente o fechamento para quem está usando o projeto, em linguagem simples, natural e direta. Use no máximo duas frases e ${ACTION_RESPONSE_MAX_CHARS} caracteres. Diga o que mudou no ambiente com nomes que a pessoa reconhece. Não repita nomes internos de ações nem expressões como “por relação”, “estado de partes móveis”, “conferência geométrica”, coordenadas, chamadas ou sequência de operações. Confirme apenas o que foi comprovado e mencione somente uma adaptação, medida ou pendência realmente relevante. Não use lista, Markdown nem reticências.`;
}

function normalizeActionResponse(text: string): string {
  return text
    .replace(/```[\s\S]*?```/g, "")
    .replace(/^\s{0,3}[-*#>]\s*/gm, "")
    .replace(/\s+/g, " ")
    .trim();
}

function actionSentences(text: string): string[] {
  return text.split(/(?<=[!?])\s+|(?<=\.)\s+(?=[A-ZÁÉÍÓÚÂÊÔÃÕÇ])/u).filter(Boolean);
}

export function compactActionAssistant(message: AgentModelResponse): AgentModelResponse {
  const text = compactActionResponse(assistantText(message)) || "Concluído.";
  let textInserted = false;
  const content: AgentModelResponse["content"] = [];
  for (const block of message.content) {
    if (block.type !== "text") {
      content.push(block);
    } else if (!textInserted) {
      textInserted = true;
      content.push({ type: "text", text });
    }
  }
  if (!textInserted) content.push({ type: "text", text });
  return {
    ...message,
    content,
  };
}

export function deterministicActionSummary(input: {
  actions: string[];
  completed: boolean;
  pending?: string[];
  reason?: string;
}): string {
  const uniqueActions = [...new Set(input.actions.map((action) => action.trim()).filter(Boolean))];
  const pending = [...new Set((input.pending ?? []).map((item) => item.trim()).filter(Boolean))];
  const naturalResults = naturalActionResults(uniqueActions);
  const result = input.completed
    ? uniqueActions.length
      ? `Pronto! ${capitalize(joinNatural([...naturalResults, "conferi o resultado no projeto"]))}.`
      : "Pronto! Conferi o pedido no projeto e está tudo certo."
    : uniqueActions.length
      ? `Até aqui, ${joinNatural(naturalResults)}.`
      : "Não apliquei alterações sem evidência suficiente.";
  // A justificativa de uma conferência concluída é dado de controle interno.
  // Só exibimos motivo e pendências quando a tarefa realmente ficou incompleta.
  const qualification = input.completed
    ? ""
    : pending.length
    ? input.reason
      ? `${input.reason.replace(/[.!?]+$/u, "")}; ficou pendente: ${pending.slice(0, 3).join(", ")}.`
      : `Ficou pendente: ${pending.slice(0, 3).join(", ")}.`
    : input.reason
      ? `${input.reason.replace(/[.!?]+$/u, "")}.`
      : "";
  return compactActionResponse(`${result} ${qualification}`.trim()) || "Concluído.";
}

function naturalActionResults(actions: string[]): string[] {
  const results: string[] = [];
  let hasUnmappedAction = false;

  for (const action of actions) {
    const rule = ACTION_RESULT_RULES.find((candidate) => candidate.pattern.test(action));
    if (rule) {
      if (!results.includes(rule.result)) results.push(rule.result);
    } else {
      hasUnmappedAction = true;
    }
  }

  const concise = results.slice(0, hasUnmappedAction ? 2 : 3);
  if (hasUnmappedAction) {
    concise.push(concise.length ? "fiz os demais ajustes solicitados" : "fiz os ajustes solicitados");
  }
  return concise.length ? concise : ["fiz os ajustes solicitados"];
}

function joinNatural(items: string[]): string {
  if (items.length <= 1) return items[0] ?? "";
  if (items.length === 2) return `${items[0]} e ${items[1]}`;
  return `${items.slice(0, -1).join(", ")} e ${items.at(-1)}`;
}

function capitalize(text: string): string {
  return text ? `${text.charAt(0).toLocaleUpperCase("pt-BR")}${text.slice(1)}` : text;
}
